// 유니온 API 연동 유틸리티
import api, { getStoreIdFromToken, authHeaders } from './api';

const UNION_API_CONFIG = {
  apiKey: '',
  // 유니온 API 코드: 와우오더가 유니온에서 부여받은 회사 단일 코드.
  // 매장/로그인 토큰과 무관하게 항상 이 값 고정 (토큰의 storeCode 를 읽으면
  // 다른 POS 매장 관리자로 로그인 시 그 매장 코드로 오염되어 E00008 발생).
  storeCode: '3000000072415',
  baseUrl: 'https://api.unionpos.kr/api/v2'
};

/**
 * 유니온 API에서 카테고리 목록을 가져옵니다
 * @returns {Promise<Array>} 카테고리 목록
 */
export const fetchUnionCategories = async () => {
  // apiKey는 서버 환경변수 → backend 프록시 경유
  const url = `/api/union/categories`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', ...authHeaders()
      },
      body: JSON.stringify({
        storeCode: UNION_API_CONFIG.storeCode
      })
    });

    const data = await response.json();

    console.log('=== Union API 카테고리 응답 데이터 ===');
    console.log('전체 응답:', JSON.stringify(data, null, 2));

    if (data.CODE === 'S00000') {
      // 성공적으로 데이터를 받았을 때
      const grpList = data.DATA?.GrpList || [];

      console.log('GrpList 원본 데이터:');
      grpList.forEach((grp, index) => {
        console.log(`${index + 1}. GrpCode: ${grp.GrpCode}, GrpName: "${grp.GrpName}", Lineup: ${grp.GrpLineup}, UseYN: ${grp.OrderUseYN}`);
      });
      
      // 모든 카테고리를 일반 카테고리로 처리 (호출 카테고리도 포함)
      const validCategories = grpList
        .filter(grp => grp.OrderUseYN === 'Y')
        .sort((a, b) => a.GrpLineup - b.GrpLineup)
        .map(grp => ({
          name: grp.GrpName,
          code: grp.GrpCode,
          lineup: grp.GrpLineup
        }));

      console.log('필터링된 카테고리:');
      validCategories.forEach((cat, index) => {
        console.log(`${index + 1}. name: "${cat.name}", code: ${cat.code}`);
      });
      console.log('=== 카테고리 응답 데이터 처리 완료 ===');

      return {
        success: true,
        categories: validCategories, // 모든 카테고리 반환
        message: data.MSG
      };
    } else {
      // 에러 코드가 반환된 경우
      return {
        success: false,
        categories: [],
        message: data.MSG || '카테고리 조회 실패'
      };
    }
  } catch (error) {
    console.error('Union API Error:', error);
    return {
      success: false,
      categories: [],
      message: '네트워크 오류가 발생했습니다.'
    };
  }
};

/**
 * 유니온 API에서 테이블 주문 정보를 조회합니다
 * @param {string} tableNo - 테이블 번호
 * @returns {Promise<Object>} 주문 정보
 */
export const fetchTableOrderDetail = async (tableNo) => {
  // apiKey는 서버 환경변수 → backend 프록시 경유 (4/7 보안 커밋 이후 직접 호출은 401)
  const url = `/api/union/table/order/detail/inside`;

  const requestBody = {
    storeCode: UNION_API_CONFIG.storeCode,
    tableCode: tableNo
  };

  console.log('📤 Union API 주문 조회 요청:', { url, body: requestBody, tableNo });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();

    if (data.success) {
      return {
        success: true,
        orderSum: data.orderSum || null,
        orderList: data.orderList || [],
        message: data.message
      };
    }
    return {
      success: false,
      orderSum: null,
      orderList: [],
      message: data.message || '주문 조회 실패'
    };
  } catch (error) {
    console.error('Union API Error:', error);
    return {
      success: false,
      orderSum: null,
      orderList: [],
      message: '네트워크 오류가 발생했습니다.'
    };
  }
};

/**
 * 유니온 API에서 테이블 주문 정보 존재 여부를 체크합니다
 * @param {string} tableNo - 테이블 번호
 * @returns {Promise<Object>} 주문 존재 여부
 */
export const checkTableOrderExists = async (tableNo = '1') => {
  const url = `/api/union/table/order/check`;

  console.log('🔍 Union API 테이블 주문 체크 요청:', {
    tableNo,
    storeCode: UNION_API_CONFIG.storeCode
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        storeCode: UNION_API_CONFIG.storeCode,
        tableCode: tableNo
      })
    });

    const result = await response.json();
    console.log(`테이블 ${tableNo} 주문 존재 여부: ${result.hasOrder ? '있음' : '없음'}`);
    return result;
  } catch (error) {
    console.error('Union API Error:', error);
    return {
      success: false,
      hasOrder: false,
      message: '네트워크 오류가 발생했습니다.'
    };
  }
};

/**
 * 유니온 API에서 상품 이미지 목록을 가져옵니다
 * @returns {Promise<Object>} 이미지 목록
 */
export const fetchUnionItemImages = async () => {
  // apiKey는 서버 환경변수 → backend 프록시 경유
  const url = `/api/union/item-images`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', ...authHeaders()
      },
      body: JSON.stringify({
        storeCode: UNION_API_CONFIG.storeCode
      })
    });

    const data = await response.json();
    
    if (data.CODE === 'S00000') {
      const imageList = data.DATA?.ItemImageList || []; // ItemImageList로 변경
      
      // ItemCode를 키로 하는 이미지 맵 생성
      const imageMap = {};
      imageList.forEach(img => {
        if (img.ItemCode && img.ImagePath) {
          imageMap[img.ItemCode] = img.ImagePath;
        }
      });
      
      console.log('Image Map created:', Object.keys(imageMap).length, 'images');
      
      return {
        success: true,
        imageMap: imageMap,
        message: data.MSG
      };
    } else {
      return {
        success: false,
        imageMap: {},
        message: data.MSG || '이미지 조회 실패'
      };
    }
  } catch (error) {
    console.error('Union API Error:', error);
    return {
      success: false,
      imageMap: {},
      message: '네트워크 오류가 발생했습니다.'
    };
  }
};

/**
 * 유니온 API에서 전체 상품 목록을 가져옵니다
 * @returns {Promise<Object>} 상품 목록
 */
export const fetchAllUnionItems = async () => {
  // apiKey는 서버 환경변수 → backend 프록시 경유
  const url = `/api/union/items`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', ...authHeaders()
      },
      body: JSON.stringify({
        storeCode: UNION_API_CONFIG.storeCode
      })
    });

    const data = await response.json();
    
    if (data.CODE === 'S00000') {
      const itemList = data.DATA?.ItemList || [];
      
      // 상품 데이터를 우리 프로젝트 형식으로 변환하고 GrpCode별로 그룹화
      const itemsByGroup = {};

      itemList.forEach(item => {
        let itemPrice = item.Price || 0; // 기본 가격 설정
        // MenuType에 따른 옵션 설정
        let hasOptions = false;
        let options = [];
        
        // MenuType 확인
        const menuType = item.MenuType;
        
        if (menuType === '변동세트') {
          // 변동세트는 SetMenuList 안에 다양한 옵션이 있을 수 있음
          if (item.SetMenuList && item.SetMenuList.length > 0) {
            hasOptions = true;
            options = [];

            // SetCnt 값을 확인하여 선택 방식 결정
            // SetCnt가 모두 1이면 라디오 버튼 (단일 선택)
            // SetCnt가 0 또는 다른 값이면 체크박스 (다중 선택)
            const allSingleSelect = item.SetMenuList.every(setItem => setItem.SetCnt === 1);

            // SetMenuList의 각 항목을 처리
            item.SetMenuList.forEach(setItem => {
              // 중첩된 선택 메뉴가 있는 경우
              if (setItem.InMenuType === '선택' && setItem.ChoiceMenuList) {
                options.push({
                  name: setItem.ItemName || "옵션 선택",
                  required: setItem.SubCol1 === 'ESSENTIAL', // ESSENTIAL이면 필수
                  maxSelect: setItem.SetCnt || 1,
                  choices: setItem.ChoiceMenuList.map(choiceItem => ({
                    name: choiceItem.ItemName || '옵션',
                    price: String(choiceItem.Price || 0),
                    itemCode: choiceItem.ItemCode
                  }))
                });
              } else {
                // 일반 변동세트 옵션
                // SetCnt에 따라 라디오 버튼 또는 체크박스로 표시
                const optionName = allSingleSelect ? "옵션 선택" : "추가 선택";

                if (!options.find(opt => opt.name === optionName)) {
                  options.push({
                    name: optionName,
                    required: allSingleSelect, // 라디오 버튼이면 필수 선택
                    isRadio: allSingleSelect, // 라디오 버튼 여부 플래그
                    maxSelect: allSingleSelect ? 1 : undefined, // 라디오는 1개만 선택
                    choices: []
                  });
                }
                const targetOption = options.find(opt => opt.name === optionName);
                targetOption.choices.push({
                  name: setItem.ItemName || '옵션',
                  price: String(setItem.Price || 0),
                  itemCode: setItem.ItemCode,
                  quantity: setItem.Qty || 1,
                  setCnt: setItem.SetCnt // SetCnt 값도 저장
                });
              }
            });
          }
        } else if (menuType === '선택') {
          // 선택 메뉴: 여러 옵션 중 하나만 선택 (세트가 아님)
          // 메인 메뉴 가격은 무시되고, 선택한 옵션의 가격만 적용
          if (item.ChoiceMenuList && item.ChoiceMenuList.length > 0) {
            hasOptions = true;
            // 선택 메뉴는 메인 가격을 0으로 설정
            itemPrice = 0;
            options = [{
              name: "옵션", // 옵션 이름 고정
              required: true, // 라디오 버튼은 필수 선택
              isChoiceType: true, // 선택 타입 플래그 추가
              choices: item.ChoiceMenuList.map(choiceItem => ({
                name: choiceItem.ItemName || '옵션',
                price: String(choiceItem.Price || 0),
                itemCode: choiceItem.ItemCode,
                menuCode: choiceItem.MenuCode
              }))
            }];
          }
        } else if (menuType === '고정세트') {
          // 고정세트: API의 메인 메뉴 가격을 그대로 사용
          // (고정세트는 메인 메뉴 가격에 구성품이 모두 포함되어 있음)

          if (item.SetMenuList && item.SetMenuList.length > 0) {
            // 고정세트 구성품을 저장 (주문 시 사용, UI에는 표시 안 함)
            options = [{
              name: "옵션", // 옵션 이름 통일
              required: true, // 고정세트는 모든 구성품 포함
              hidden: true, // UI에 표시하지 않음
              choices: item.SetMenuList.map(setItem => ({
                name: setItem.ItemName || '옵션',
                price: String(setItem.Price || 0),
                itemCode: setItem.ItemCode, // Union API의 ItemCode 사용
                quantity: setItem.Qty || 1 // 수량 정보도 저장
              }))
            }];

            // 고정세트는 API의 Price를 그대로 사용
            // (메인 메뉴 가격에 이미 구성품이 포함되어 있음)
            itemPrice = item.Price || 0;

            console.log(`고정세트 "${item.ItemName}" API 가격: ${itemPrice}원 (구성품 포함 가격)`);

            // 밥 고정세트 특별 디버깅
            if (item.ItemName === '밥') {
              console.log('🔍 === 밥 고정세트 Union API 원본 ===');
              console.log('ItemCode:', item.ItemCode);
              console.log('ItemName:', item.ItemName);
              console.log('Price (API 원본):', item.Price);
              console.log('itemPrice (저장할 값):', itemPrice);
              console.log('SetMenuList:', item.SetMenuList);
              console.log('================================');
            }
          }
        }
        // '고정세트'인 경우는 옵션 없음 (hasOptions = false 유지)
        
        // DB에서 가져온 데이터를 사용해야 하므로 기존 설명은 메뉴 이름을 기본값으로
        const getDescription = (lang) => {
          return item.ItemName; // 설명이 없으면 메뉴 이름을 기본값으로
        };
        
        const formattedItem = {
          id: `union-${item.ItemCode}`,
          ko: {
            name: item.ItemName,
            description: getDescription('ko'),
            price: menuType === '선택' ? '0원' : `${itemPrice.toLocaleString()}원`
          },
          en: {
            name: item.ItemName,
            description: getDescription('en'),
            price: menuType === '선택' ? '₩0' : `₩${itemPrice.toLocaleString()}`
          },
          ja: {
            name: item.ItemName,
            description: getDescription('ja'),
            price: menuType === '선택' ? '₩0' : `₩${itemPrice.toLocaleString()}`
          },
          zh: {
            name: item.ItemName,
            description: getDescription('zh'),
            price: menuType === '선택' ? '₩0' : `₩${itemPrice.toLocaleString()}`
          },
          soldOut: item.SoldOut === 'Y', // 품절 여부
          price: itemPrice,  // API에서 받은 실제 가격 저장
          basePrice: itemPrice,  // basePrice도 동일하게 설정
          hasOptions: hasOptions,
          options: options,
          menuType: menuType, // MenuType 저장
          tags: (() => {
            const tags = [];
            if (item.ItemTagHot === 'Y') tags.push('hot');
            if (item.ItemTagBest === 'Y') tags.push('popular');
            if (item.ItemTagNew === 'Y') tags.push('new');
            if (item.ItemTagDc === 'Y') tags.push('sale');
            if (item.ItemTagPick === 'Y') tags.push('recommend');
            if (item.ItemTagEvent === 'Y') tags.push('event');
            return tags;
          })(),
          openTime: null, // 기본값: 판매시간 제한 없음
          closeTime: null, // 기본값: 판매시간 제한 없음
          image: null, // 기본 이미지 제거
          itemCode: item.ItemCode // 이미지 매칭을 위해 ItemCode 저장
        };
        
        // GrpCode별로 그룹화
        if (!itemsByGroup[item.GrpCode]) {
          itemsByGroup[item.GrpCode] = [];
        }
        itemsByGroup[item.GrpCode].push(formattedItem);
      });
      
      return {
        success: true,
        itemsByGroup: itemsByGroup,
        totalItems: itemList.length,
        message: data.MSG
      };
    } else {
      return {
        success: false,
        itemsByGroup: {},
        totalItems: 0,
        message: data.MSG || '상품 조회 실패'
      };
    }
  } catch (error) {
    console.error('Union API Error:', error);
    return {
      success: false,
      itemsByGroup: {},
      totalItems: 0,
      message: '네트워크 오류가 발생했습니다.'
    };
  }
};

// extractCallReasons 함수 제거됨 - 호출 카테고리는 일반 메뉴처럼 처리


/**
 * 전체 동기화 프로세스
 * 카테고리와 모든 상품을 한번에 가져옵니다
 */
export const syncAllUnionData = async (onProgress) => {
  try {
    // 1. 카테고리 가져오기
    if (onProgress) onProgress('카테고리 정보를 가져오는 중...');
    const categoryResult = await fetchUnionCategories();

    if (!categoryResult.success) {
      return {
        success: false,
        message: categoryResult.message
      };
    }

    // 2. 상품 정보 가져오기
    if (onProgress) onProgress('상품 정보를 가져오는 중...');
    const itemResult = await fetchAllUnionItems();

    if (!itemResult.success) {
      return {
        success: false,
        message: itemResult.message
      };
    }

    // 3. 상품 이미지 가져오기
    if (onProgress) onProgress('상품 이미지를 가져오는 중...');
    const imageResult = await fetchUnionItemImages();
    
    // 이미지 조회 실패해도 계속 진행 (이미지는 선택사항)

    // 4. 번역 데이터 DB에 초기화 (모든 언어를 한국어로)
    if (onProgress) onProgress('번역 데이터를 초기화하는 중...');

    // 카테고리 번역 데이터 준비
    const categoryTranslations = categoryResult.categories
      .filter(cat => !cat.isCallCategory) // 호출 카테고리 제외
      .map(cat => ({
        grpCode: cat.code,
        name: cat.name
      }));

    // 메뉴 번역 데이터 준비
    const menuTranslations = [];
    Object.entries(itemResult.itemsByGroup).forEach(([grpCode, items]) => {
      items.forEach(item => {
        // Union API 원본 데이터에서 ItemCode 추출
        const itemCode = item.id ? item.id.replace('union-', '') : null;
        if (itemCode) {
          menuTranslations.push({
            itemCode: itemCode,
            name: item.ko?.name || item.name || '',
            description: item.ko?.description || ''
          });
        }
      });
    });

    // 번역 데이터 DB에 저장
    try {
      // 예전엔 storeId 가 1 로 하드코딩돼 있어, 어느 매장에서 동기화하든 번역이
      // store 1 에 저장됐다(남의 매장 데이터 오염). 로그인 토큰의 매장으로 바로잡는다.
      const storeId = getStoreIdFromToken();
      if (!storeId) {
        console.error('[번역 동기화] 로그인 매장을 확인할 수 없습니다.');
        return { success: false, message: '로그인이 필요합니다.' };
      }
      const response = await fetch('/api/translations/sync/' + storeId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          categories: categoryTranslations,
          menus: menuTranslations
        })
      });

      const result = await response.json();
      if (!result.success) {
        console.error('번역 데이터 초기화 실패:', result.message);
      } else {
        console.log('번역 데이터 초기화 성공');
      }
    } catch (error) {
      console.error('번역 데이터 초기화 중 오류:', error);
    }
    const imageMap = imageResult.success ? imageResult.imageMap : {};
    
    // 4. 카테고리와 상품 매칭하여 저장
    if (onProgress) onProgress('데이터를 정리하는 중...');
    
    // DB에서 카테고리 정보를 가져와야 하므로 첫 동기화로 간주
    let existingCategories = [];
    let isFirstSync = true;
    
    // 디버깅 로그
    console.log('=== 카테고리 동기화 시작 ===');
    console.log('기존 카테고리:', existingCategories);
    console.log('새로운 카테고리:', categoryResult.categories);
    console.log('첫 동기화 여부:', isFirstSync);
    
    let finalCategories = [];
    
    if (isFirstSync || existingCategories.length === 0) {
      // 첫 동기화거나 기존 카테고리가 없는 경우: 모든 카테고리를 순서대로 추가
      finalCategories = categoryResult.categories.map((cat, index) => ({
        name: cat.name,
        grpCode: cat.code,
        order: index + 1
      }));
      console.log('첫 동기화: 모든 카테고리 추가');
    } else {
      // 기존 카테고리가 있는 경우: GrpCode 기준으로 매칭
      const updatedCategories = [];
      const newCategories = [];
      
      // 기존 카테고리의 순서 유지하면서 업데이트
      existingCategories.forEach(existingCat => {
        // GrpCode로 새 데이터에서 매칭되는 카테고리 찾기
        const matchingNewCat = categoryResult.categories.find(
          newCat => newCat.code === existingCat.grpCode
        );
        
        if (matchingNewCat) {
          // 기존 카테고리가 새 데이터에도 있으면 이름만 업데이트
          console.log(`카테고리 업데이트: "${existingCat.name}" → "${matchingNewCat.name}" (${existingCat.grpCode})`);
          updatedCategories.push({
            name: matchingNewCat.name, // 새 이름으로 업데이트
            grpCode: matchingNewCat.code,
            order: existingCat.order // 기존 순서 유지
          });
        } else if (existingCat.grpCode) {
          // GrpCode가 있지만 새 데이터에 없는 경우 (삭제된 카테고리)
          console.log(`카테고리 제거됨: ${existingCat.name} (${existingCat.grpCode})`);
        } else {
          // GrpCode가 없는 수동 추가 카테고리는 유지
          console.log(`수동 추가 카테고리 유지: ${existingCat.name}`);
          updatedCategories.push(existingCat);
        }
      });
      
      // 새로 추가된 카테고리 찾기
      categoryResult.categories.forEach(newCat => {
        const exists = existingCategories.some(
          existingCat => existingCat.grpCode === newCat.code
        );
        
        if (!exists) {
          // 새 카테고리는 마지막에 추가
          const maxOrder = Math.max(
            ...updatedCategories.map(c => c.order || 0),
            ...existingCategories.map(c => c.order || 0),
            0
          );
          
          newCategories.push({
            name: newCat.name,
            grpCode: newCat.code,
            order: maxOrder + newCategories.length + 1
          });
          
          console.log(`새 카테고리 추가됨: ${newCat.name} (${newCat.code})`);
        }
      });
      
      // 기존 카테고리 + 새 카테고리 합치기
      finalCategories = [...updatedCategories, ...newCategories];
    }
    
    // 순서대로 정렬
    finalCategories.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    console.log('최종 카테고리:', finalCategories);
    console.log('=== 카테고리 동기화 완료 ===');
    
    // DB로만 작동 - localStorage 사용 안 함
    const categoryNames = finalCategories.map(cat => cat.name);

    // DB에서 메뉴 데이터를 가져와야 하므로 빈 객���로 시작
    let existingMenuData = {};
    
    // menuData 생성 - 각 카테고리에 해당하는 상품 매핑
    const menuData = {};
    let totalItemsAdded = 0;
    
    console.log('=== 메뉴 동기화 시작 ===');
    
    // finalCategories를 사용하여 순서대로 메뉴 데이터 생성
    finalCategories.forEach(category => {
      const categoryName = category.name;
      const grpCode = category.grpCode;
      
      // 해당 GrpCode의 상품들 가져오기
      let items = itemResult.itemsByGroup[grpCode] || [];
      
      // DB에서만 데이터를 사용하므로 기존 데이터 병합 없음
      const existingItemsMap = {};
      
      // 각 아이템 처리 (DB에서만 데이터를 사용하므로 병합 없이 이미지만 매칭)
      items = items.map(item => {
        const itemCode = item.itemCode;

        // 이미지 매칭
        let finalImage = item.image;
        if (itemCode && imageMap[itemCode]) {
          finalImage = imageMap[itemCode];
        }

        // 모든 아이템을 새 아이템으로 처리
        console.log(`메뉴 추가: ${item.ko.name} (${itemCode})`);
        return {
          ...item,
          image: finalImage
        };
      });
      
      menuData[categoryName] = items;
      totalItemsAdded += items.length;
    });
    
    console.log('=== 메뉴 동기화 완료 ===');

    // 5. 테이블 목록 동기화
    if (onProgress) onProgress('테이블 목록을 동기화하는 중...');
    try {
      const storeId = getStoreIdFromToken(); // 로그인한 사용자의 storeId 사용
      console.log('[테이블 동기화] storeId:', storeId, 'storeCode:', UNION_API_CONFIG.storeCode);
      const tableResponse = await fetch('/api/union/sync-tables', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', ...authHeaders()
        },
        body: JSON.stringify({
          storeId: storeId,
          storeCode: UNION_API_CONFIG.storeCode
        })
      });

      const tableResult = await tableResponse.json();
      if (!tableResult.success) {
        console.error('테이블 동기화 실패:', tableResult.error);
      } else {
        console.log(`테이블 ${tableResult.count}개 동기화 성공`);
      }
    } catch (tableError) {
      console.error('테이블 동기화 중 오류:', tableError);
      // 테이블 동기화 실패해도 계속 진행
    }

    // DB에 저장
    if (onProgress) onProgress('데이터베이스에 저장 중...');

    try {
      // DB 저장용 데이터 형식으로 변환
      const dbSyncData = {
        categories: [],
        callReasons: []
      };

      // 카테고리와 메뉴 데이터 변환
      finalCategories.forEach((category, index) => {
        const categoryData = {
          code: category.grpCode || category.code,  // grpCode 또는 code 사용
          name: category.name,
          order: index,
          menus: []
        };

        // 해당 카테고리의 메뉴들 추가
        const menus = menuData[category.name] || [];
        menus.forEach(menu => {
          // 가격 파싱 (문자열에서 숫자만 추출)
          let price = 0;

          // 선택 메뉴는 가격을 0으로 설정
          if (menu.menuType === '선택') {
            price = 0;
          }
          // 고정세트는 basePrice나 price를 우선 사용 (API에서 받은 정확한 가격)
          else if (menu.menuType === '고정세트') {
            // 밥 고정세트 디버깅
            if (menu.ko?.name === '밥' || menu.name === '밥') {
              console.log('🔍 === 밥 고정세트 Frontend 가격 추적 ===');
              console.log('menu.basePrice:', menu.basePrice);
              console.log('menu.price:', menu.price);
              console.log('menu.ko?.price:', menu.ko?.price);
            }

            // 고정세트는 basePrice 또는 price (숫자) 사용
            if (menu.basePrice !== undefined && menu.basePrice !== null) {
              price = typeof menu.basePrice === 'number' ? menu.basePrice : parseInt(menu.basePrice);
            } else if (menu.price !== undefined && menu.price !== null) {
              price = typeof menu.price === 'number' ? menu.price : parseInt(menu.price.toString().replace(/[^0-9]/g, ''));
            } else if (menu.ko?.price) {
              // 마지막 대안으로만 사용 (이 경우 문제가 될 수 있음)
              console.warn(`⚠️ 고정세트 ${menu.ko?.name}가 basePrice/price 없이 ko.price만 사용: ${menu.ko.price}`);
              price = parseInt(menu.ko.price.replace(/[^0-9]/g, ''));
            }

            if (menu.ko?.name === '밥' || menu.name === '밥') {
              console.log('최종 전송 가격:', price);
              console.log('================================');
            }
          }
          // 일반 메뉴
          else if (menu.basePrice) {
            price = parseInt(menu.basePrice);
          } else if (menu.ko?.price) {
            price = parseInt(menu.ko.price.replace(/[^0-9]/g, ''));
          } else if (menu.price) {
            price = typeof menu.price === 'string' ? parseInt(menu.price.replace(/[^0-9]/g, '')) : menu.price;
          }

          const menuItem = {
            code: menu.itemCode || menu.id || menu.code || `MENU_${Date.now()}_${Math.random()}`,
            name: menu.ko?.name || menu.name || '',
            price: price,
            description: menu.ko?.description || menu.description || '',
            imageUrl: menu.image || menu.imageUrl || null,
            options: menu.options || [],
            menuType: menu.menuType || null
          };

          // 고정세트 디버깅
          if (menu.menuType === '고정세트' && menu.ko?.name === '밥') {
            console.log(`✅ 고정세트 ${menu.ko?.name} - 전송 price: ${price}, options 포함 (주문 시 필요)`);
          }

          categoryData.menus.push(menuItem);
        });

        dbSyncData.categories.push(categoryData);
      });

      // 호출 사유는 더 이상 Union 동기화로 처리하지 않음
      // 관리자 페이지에서 직접 관리

      // API 호출하여 DB에 저장
      const dbResponse = await api.syncUnion(dbSyncData);

      if (!dbResponse.success) {
        console.error('DB 저장 실패:', dbResponse.message);
      } else {
        console.log('DB 저장 성공');
      }
    } catch (dbError) {
      console.error('DB 저장 중 오류:', dbError);
      // DB 저장 실패해도 계속 진행
    }

    // storage 이벤트 발생
    window.dispatchEvent(new Event('storage'));

    if (onProgress) onProgress('동기화 완료!');

    return {
      success: true,
      message: `${categoryResult.categories.length}개 카테고리, ${totalItemsAdded}개 메뉴를 동기화했습니다.`,
      categories: categoryResult.categories,
      totalItems: totalItemsAdded
    };
    
  } catch (error) {
    console.error('Sync Error:', error);
    return {
      success: false,
      message: '동기화 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 장바구니 데이터를 Union POS API 주문 형식으로 변환
 * @param {Array} cartItems - 장바구니 아이템 목록
 * @param {String} tableNo - 테이블 번호
 * @param {String} lang - 언어 설정 (KO, EN, JA, ZH)
 * @returns {Object} Union API 주문 데이터
 */
export const formatOrderForUnion = (cartItems, tableNo = '1', lang = 'KO') => {
  console.log('=== formatOrderForUnion 시작 ===');
  console.log('입력받은 cartItems:', cartItems);
  console.log('테이블 번호:', tableNo);
  console.log('언어:', lang);
  
  const orderData = [];
  let orderQty = 0;
  
  cartItems.forEach((cartItem, index) => {
    console.log(`\n--- 아이템 ${index + 1} 처리 중 ---`);
    console.log('cartItem 전체:', cartItem);

    // 선택 메뉴에서 생성된 독립 아이템인지 확인
    const isFromChoiceMenu = cartItem.isFromChoiceMenu;
    console.log('선택 메뉴에서 생성된 아이템:', isFromChoiceMenu);

    // 각 장바구니 아이템을 Union 형식으로 변환
    const item = cartItem.item;
    const quantity = cartItem.quantity;

    console.log('메뉴 정보:', item);
    console.log('수량:', quantity);
    console.log('ItemCode:', item.itemCode);
    console.log('메뉴 타입:', item.menuType);
    
    // 기본 주문 아이템 구조
    const orderItem = {
      ItemCode: item.itemCode || item.menu_code || item.id?.replace('union-', ''), // itemCode, menu_code 순으로 확인
      Qty: String(quantity),
      IsPack: 'C', // 기본값: 좌석 (C = 좌석, P = 포장)
      Memo: '' // 주문 메모 (필요시 추가)
    };
    
    console.log('기본 orderItem:', orderItem);
    
    // 선택 메뉴에서 생성된 독립 아이템은 가격 설정 없이 itemCode만 전송
    if (isFromChoiceMenu) {
      console.log('선택 메뉴에서 생성된 독립 아이템 - itemCode만 전송');
      console.log('ItemCode로 주문:', orderItem.ItemCode);
      // 가격 정보는 설정하지 않음 - POS에서 원본 메뉴 가격 자동 적용
      orderItem._qtyContribution = quantity;
      orderData.push(orderItem);
      orderQty += quantity;
      return; // 다음 아이템으로
    }

    // 가격 정보 추가 (변동세트, 시세변동 상품의 경우만)
    if (item.menuType === '변동세트' || item.menuType === '시세변동') {
      // 가격이 숫자 또는 문자열일 수 있음
      const priceValue = item.basePrice || item.ko?.price || item.price || 0;
      let price = 0;
      if (typeof priceValue === 'number') {
        price = priceValue;
      } else if (typeof priceValue === 'string') {
        price = parseInt(priceValue.replace(/[^0-9]/g, ''), 10);
      }
      orderItem.Price = String(price * quantity);
    }

    // 고정세트 메뉴 처리
    if (item.menuType === '고정세트') {
      console.log('고정세트 메뉴 처리');

      orderItem.SetMenu = [];

      // 고정세트는 항상 메뉴의 전체 구성품(item.options)을 사용
      // selectedOptions는 무시 (UI에서 자동 선택된 일부만 포함되므로)
      let selectedOptionsArray = [];
      if (item.options && Array.isArray(item.options)) {
        item.options.forEach(optionGroup => {
          if (optionGroup.choices && Array.isArray(optionGroup.choices)) {
            optionGroup.choices.forEach(choice => {
              if (choice.itemCode) {
                selectedOptionsArray.push(choice);
              }
            });
          }
        });
      }

      console.log('고정세트: 전체 구성품 로드:', selectedOptionsArray.length, '개');
      console.log('고정세트 옵션 배열:', selectedOptionsArray);

      // 고정세트의 각 구성품을 SetMenu로 추가
      selectedOptionsArray.forEach((option, optIdx) => {
        if (option.itemCode) {
          const setMenuItem = {
            SetCode: option.itemCode,
            SetQty: String(option.quantity || quantity)
          };
          console.log(`고정세트 구성품 ${optIdx + 1}:`, setMenuItem);
          orderItem.SetMenu.push(setMenuItem);
        }
      });

      orderItem._qtyContribution = quantity;
      orderData.push(orderItem);
      orderQty += quantity;
      return; // 다음 아이템으로
    }

    // 옵션이 있는 경우 SetMenu 배열 추가 (일반 메뉴, 변동세트)
    console.log('선택된 옵션:', cartItem.selectedOptions);

    // selectedOptions가 배열인지 객체인지 확인하고 처리
    let selectedOptionsArray = [];

    if (cartItem.selectedOptions) {
      if (Array.isArray(cartItem.selectedOptions)) {
        // 이미 배열 형태인 경우
        selectedOptionsArray = cartItem.selectedOptions;
      } else if (typeof cartItem.selectedOptions === 'object') {
        // 객체 형태인 경우 배열로 변환
        Object.entries(cartItem.selectedOptions).forEach(([key, value]) => {
          if (value) {
            if (Array.isArray(value)) {
              // 다중 선택 옵션
              value.forEach(opt => {
                if (opt && opt.name) {
                  selectedOptionsArray.push(opt);
                }
              });
            } else if (typeof value === 'object' && value.name) {
              // 단일 선택 옵션
              selectedOptionsArray.push(value);
            }
          }
        });
      }
    }
    
    console.log('변환된 옵션 배열:', selectedOptionsArray);
    
    if (selectedOptionsArray.length > 0) {
      orderItem.SetMenu = [];
      let totalOptionPrice = 0;
      
      selectedOptionsArray.forEach((option, optIdx) => {
        console.log(`옵션 ${optIdx + 1}:`, option);
        // 옵션의 itemCode가 있는 경우만 추가
        if (option.itemCode) {
          const optionPrice = parseInt(option.price || '0', 10);
          const setMenuItem = {
            SetCode: option.itemCode,
            SetQty: 1, // 옵션 수량은 기본 1
            Price: optionPrice
          };
          console.log('SetMenu 아이템 추가:', setMenuItem);
          orderItem.SetMenu.push(setMenuItem);
          totalOptionPrice += optionPrice;
        } else {
          console.log('옵션에 itemCode가 없음:', option);
        }
      });
      
      // 옵션이 있는 경우 총 가격 계산 (기본 가격 + 옵션 가격)
      if (orderItem.SetMenu.length > 0) {
        const priceValue = item.basePrice || item.ko?.price || item.price || 0;
        let basePrice = 0;
        if (typeof priceValue === 'number') {
          basePrice = priceValue;
        } else if (typeof priceValue === 'string') {
          basePrice = parseInt(priceValue.replace(/[^0-9]/g, ''), 10);
        }
        orderItem.Price = String((basePrice + totalOptionPrice) * quantity);
      }
    }
    
    orderItem._qtyContribution = 1;
    orderData.push(orderItem);
    orderQty++;
  });
  
  // 최종 주문 데이터 구조
  const orderRequest = {
    StoreCode: UNION_API_CONFIG.storeCode,
    TableCode: tableNo,
    OrderData: orderData,
    OrderQty: orderQty,
    SocketKey: `WEB_${UNION_API_CONFIG.storeCode}_${tableNo}_${new Date().toISOString().replace(/[-:T.]/g, '')}`,
    CustData: {
      CustNum: 1, // 기본 인원수
      CustType: 0,
      Num1: 1,
      Num2: 0
    },
    Lang: lang,
    ApiPosNo: '100', // 포스 번호
    Memo: ''
  };
  
  console.log('\n=== 최종 주문 페이로드 ===');
  console.log(JSON.stringify(orderRequest, null, 2));
  console.log('=========================\n');
  
  return orderRequest;
};

/**
 * Union POS API로 주문 전송
 * @param {Object} orderData - formatOrderForUnion으로 생성된 주문 데이터
 * @returns {Promise<Object>} API 응답
 */
// POS 호출 API (백엔드 프록시 사용)
// 직원 호출 — OKDC 와 동일 패턴 (자체 WebSocket).
// 백엔드가 reasonIds → 한국어 변환 후 Union 클라이언트로 forward.
export const sendCallToPOS = async (storeId, tableName, reasonIds) => {
  try {
    const response = await fetch('/api/union/staff-call', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableName, reasonIds })
    });
    const result = await response.json();
    if (!response.ok) {
      return { success: false, message: result.message || '호출 실패' };
    }
    return { success: true, callItems: result.callItems };
  } catch (error) {
    console.error('[Union] 직원 호출 오류:', error);
    return { success: false, message: error.message };
  }
};

// 테이블 사용 가능 여부 체크
export const checkTableAvailability = async (tableCode = '1') => {
  console.log('[Union API] 🔵 checkTableAvailability 호출됨, 테이블:', tableCode);
  const url = `/api/union/table/availability`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        storeCode: UNION_API_CONFIG.storeCode,
        tableCode: tableCode
      })
    });

    const result = await response.json();
    console.log('테이블 사용 가능 체크 결과:', result);
    return result;
  } catch (error) {
    console.error('테이블 체크 중 오류:', error);
    throw error;
  }
};

export const sendOrderToUnion = async (orderData, notificationItems = [], notificationTotalPrice = 0) => {
  try {
    console.log('=== Union POS 주문 전송 시작 ===');
    console.log('Store Code:', UNION_API_CONFIG.storeCode);
    console.log('Table Code:', orderData.TableCode);

    // 1단계: 테이블에 기존 주문이 있는지 확인
    const orderCheckResult = await checkTableOrderExists(orderData.TableCode);
    console.log('테이블 주문 체크 결과:', orderCheckResult);

    // 2단계: 주문 전송 (백엔드 프록시 사용)
    const response = await fetch('/api/union/order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        orderData: orderData,
        hasOrder: orderCheckResult.hasOrder || false,
        notificationItems,
        notificationTotalPrice
      })
    });

    console.log('Response status:', response.status);
    const data = await response.json();
    console.log('주문 응답:', data);

    return data;
  } catch (error) {
    console.error('Union POS 주문 오류:', error);
    return {
      success: false,
      message: '네트워크 오류가 발생했습니다.',
      error: error.message
    };
  }
};